// src/infra/crm-api/crmApi.ts
import { useAxiosInstances } from "../axios-instance/useAxiosInstances";
import type {
  CRMConfig,
  CRMConfigCreatePayload,
  CRMConfigUpdatePayload,
  ImportLeadsPayload,
  FunnelSdrConfig,
  FunnelConfigCreatePayload,
  FunnelConfigUpdatePayload,
  ListResponse,
  SingleResponse
} from "./types";

export const useCRMApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();

  const listCRMConfigs = async (companyId: string): Promise<CRMConfig[]> => {
    try {
      const resp = await axiosAuthInstance.get<ListResponse<CRMConfig>>(
        `/routes/crm/config/${companyId}`
      );
      return resp.data.result;
    } catch (error: any) {
      // Se o erro for porque não encontrou configs, retorna lista vazia silenciosamente
      if (error.response?.status === 404) {
        return [];
      }
  
      // Para qualquer outro erro (problemas de rede, 500, etc), também retorna vazio sem logar
      return [];
    }
  };

  const getCRMConfigById = async (id: string): Promise<CRMConfig> => {
    const resp = await axiosAuthInstance.get<SingleResponse<CRMConfig>>(
      `/routes/crm/config/id/${id}`
    );
    return resp.data.result;
  };

  const createCRMConfig = async (
    data: CRMConfigCreatePayload
  ): Promise<CRMConfig> => {
    const resp = await axiosAuthInstance.post<SingleResponse<CRMConfig>>("/routes/crm/config", data);
    return resp.data.result;
  };

  const updateCRMConfig = async (
    data: CRMConfigUpdatePayload
  ): Promise<CRMConfig> => {
    const resp = await axiosAuthInstance.put<SingleResponse<CRMConfig>>("/routes/crm/config", data);
    return resp.data.result;
  };

  // Import Leads (agora recebe funnelItemId e sdrItemId)
  const importLeadsApi = async (payload: ImportLeadsPayload) => {
    const {
      projectExportId,
      companyId,
      crmConfigName,
      funnelItemId,
      sdrItemId,
      userId,
      industry
    } = payload;

    if (crmConfigName === "Bitrix24") {
      return axiosAuthInstance.post(
        `/routes/crm/import-bitrix/${projectExportId}`,
        { companyId, crmName: crmConfigName, funnelItemId, sdrItemId, userId, industry }
      );
    }

    return axiosAuthInstance.post(
      `/routes/crm/import/${projectExportId}`,
      { companyId, crmName: crmConfigName, funnelItemId, sdrItemId, userId, industry }
    );
  };

  // Funnel–SDR Configs
  const listFunnelConfigs = async (
    crmConfigId: string
  ): Promise<FunnelSdrConfig[]> => {
    const resp = await axiosAuthInstance.get<ListResponse<FunnelSdrConfig>>(
      `/routes/crm/funnel-config/${crmConfigId}`
    );
    return resp.data.result;
  };

  const getFunnelConfigById = async (
    id: string
  ): Promise<FunnelSdrConfig> => {
    const resp = await axiosAuthInstance.get<SingleResponse<FunnelSdrConfig>>(
      `/routes/crm/funnel-config/id/${id}`
    );
    return resp.data.result;
  };

  const createFunnelConfig = async (
    data: FunnelConfigCreatePayload
  ): Promise<FunnelSdrConfig> => {

    const resp = await axiosAuthInstance.post<FunnelSdrConfig>(
      `/routes/crm/funnel-config`,
      data
    );
    return resp.data;
  };

  const updateFunnelConfig = async (
    data: FunnelConfigUpdatePayload
  ): Promise<FunnelSdrConfig> => {
    const resp = await axiosAuthInstance.put<FunnelSdrConfig>(
      `/routes/crm/funnel-config`,
      data
    );
    return resp.data;
  };

  return {
    listCRMConfigs,
    getCRMConfigById,
    createCRMConfig,
    updateCRMConfig,
    importLeadsApi,
    listFunnelConfigs,
    getFunnelConfigById,
    createFunnelConfig,
    updateFunnelConfig
  };
};